import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import type { DocumentMetadataUserEditEntry } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import { parseDocumentMetadataUserEditEntries } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import { documentMetadataUserEditByEntryId } from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import { documentMetadataUserEditEntryId } from 'features/knowledgeDocumentViewer/utils/metadataUserEditIds'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useMemo } from 'react'
import type { KeyedMutator } from 'swr'

export type UseDocumentMetadataUserEditArgs = {
  bqDocumentId: string | undefined
  enabled?: boolean
}

export function useDocumentMetadataUserEdit({
  bqDocumentId,
  enabled = true,
}: UseDocumentMetadataUserEditArgs): {
  edit: DocumentMetadataUserEditEntry | undefined
  isLoading: boolean
  mutate: KeyedMutator<unknown>
} {
  const { siteID } = useKnowledgeDocumentDataContext()

  const loadOptions = useMemo(() => {
    if (!enabled || !bqDocumentId || !siteID) {
      return undefined
    }
    const entryId = documentMetadataUserEditEntryId(siteID, bqDocumentId)
    return documentMetadataUserEditByEntryId(entryId, { site: siteID })
  }, [bqDocumentId, enabled, siteID])

  const {
    data: rawEntries,
    isLoading,
    mutate,
  } = useGetRolodexEntriesByLoadOptions<unknown>(loadOptions, {
    revalidateOnMount: true,
  })

  const edit = useMemo(() => {
    if (rawEntries === undefined) return undefined
    return parseDocumentMetadataUserEditEntries(rawEntries)[0]
  }, [rawEntries])

  return {
    edit,
    isLoading: enabled && loadOptions !== undefined && isLoading,
    mutate,
  }
}
